import React, { useState } from 'react';
import { Eye, EyeOff, Lock, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import API_BASE_URL from '../config/api';

interface FormData {
  username: string;
  password: string;
}

interface ApiResponse {
  message: string;
  token?: string;
  userId?: string;
}

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState<boolean>(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.currentTarget;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const validateForm = (): boolean => {
    if (!formData.username.trim()) {
      setError(t('auth.login.usernameRequired'));
      return false;
    }
    if (!formData.password) {
      setError(t('auth.login.passwordRequired'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password
        })
      });

      const data: ApiResponse = await response.json();

      if (!response.ok) {
        setError(data.message || t('auth.login.loginError'));
        return;
      }

      if (data.token) {
        localStorage.setItem('authToken', data.token);

        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
          localStorage.setItem('username', formData.username);
        }

        setSuccess(t('auth.login.loginSuccess'));

        // Vérifier s'il y a une URL de redirection sauvegardée (ex: après scan QR code)
        const redirectUrl = localStorage.getItem('redirectAfterLogin');
        console.log('URL de redirection trouvée:', redirectUrl);

        setTimeout(() => {
          if (redirectUrl) {
            // Supprimer l'URL de redirection et rediriger vers celle-ci
            console.log('Redirection vers:', redirectUrl);
            localStorage.removeItem('redirectAfterLogin');
            window.location.href = redirectUrl;
          } else {
            // Redirection par défaut vers mes tests
            console.log('Pas de redirection sauvegardée, redirection vers /my-tests');
            window.location.href = '/my-tests';
          }
        }, 1500);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('auth.login.loginError');
      setError(t('auth.login.serverError') + ': ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-2xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800">{t('auth.login.title')}</h1>
            <p className="text-gray-600 mt-2">{t('auth.login.welcome')}</p>
          </div>

          <div className="space-y-4">
            {/* Identifiant */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                {t('auth.login.username')}
              </label>
              <div className="relative">
                <User className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder={t('auth.login.enterUsername')}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Mot de passe */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                {t('auth.login.password')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={t('auth.login.enterPassword')}
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                  disabled={loading}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Se souvenir de moi et mot de passe oublié */}
            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={loading}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-600 cursor-pointer"
                />
                <span className="ml-2 text-sm text-gray-600">{t('auth.login.rememberMe')}</span>
              </label>
              <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-700 font-semibold">
                {t('auth.login.forgotPassword')}
              </Link>
            </div>

            {/* Messages d'erreur/succès */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}
            {success && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                {success}
              </div>
            )}

            {/* Bouton de connexion */}
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed mt-6"
            >
              {loading ? t('auth.login.loggingIn') : t('auth.login.loginButton')}
            </button>
          </div>

          <p className="text-center text-gray-600 text-sm mt-6">
            {t('auth.login.noAccount')}{' '}
            <a href="/signup" className="text-blue-600 hover:text-blue-700 font-semibold">
              {t('auth.login.signupLink')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;